<i18n locale="en">
{
  "provider": "Provider",
  "untappd": "Untappd",
  "source": {
    "label": "Untappd iframe URL or IDs",
    "helper": "Paste the iframe code from Untappd for Business, its business.untappd.com iframe URL, or the location/theme IDs (for example 2800/7676). Only the verified IDs are saved.",
    "placeholder": "https://business.untappd.com/embeds/iframes/2800/7676"
  },
  "location-id": "Location ID",
  "theme-id": "Theme ID",
  "detected": "Automatically detected",
  "validation": {
    "required": "Untappd information is required",
    "invalid": "Enter a valid Untappd iframe URL, iframe code, or location/theme ID pair"
  }
}
</i18n>

<template>
  <div class="flex-col gap-8" data-testid="website-integration-editor">
    <OwnInputContainer :label="t('provider')">
      <OwnInput
        :model-value="t('untappd')"
        data-testid="website-integration-provider"
        disabled
      />
    </OwnInputContainer>

    <OwnInputContainer
      :label="t('source.label')"
      :helper-text="t('source.helper')"
      :errors="mapErrorMessage(formState.source.$errors)"
      :value="source"
    >
      <OwnTextarea
        v-model="source"
        data-testid="website-integration-source"
        rows="4"
        :placeholder="t('source.placeholder')"
        @update:model-value="onSourceUpdate"
      />
    </OwnInputContainer>

    <div class="grid grid-cols-2 gap-4">
      <OwnInputContainer :label="t('location-id')" :value="locationId">
        <OwnInput
          v-model="locationId"
          data-testid="website-integration-location-id"
          disabled
          :placeholder="t('detected')"
        />
      </OwnInputContainer>
      <OwnInputContainer :label="t('theme-id')" :value="themeId">
        <OwnInput
          v-model="themeId"
          data-testid="website-integration-theme-id"
          disabled
          :placeholder="t('detected')"
        />
      </OwnInputContainer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMappedGetter } from '@/store'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnTextarea } from '@/ui/OwnTextarea'
import { mapErrorMessage } from '@/utils/vuelidate'

import type { WebsiteSection } from '../../../WebsiteBuilder.types'
import { IntegrationsCommands } from '../commands/IntegrationsCommands'
import { extractUntappdIds } from '../untappd'

const props = defineProps<{ id: string }>()
const { t } = useI18n()

const source = ref('')
const locationId = ref('')
const themeId = ref('')

const rules = computed(() => ({
  source: {
    required: helpers.withMessage(t('validation.required'), required),
    valid: helpers.withMessage(
      t('validation.invalid'),
      (value: string) => extractUntappdIds(value) !== null
    ),
  },
}))
const formState = useVuelidate(rules, { source })

const blockById = useMappedGetter<(id: string) => WebsiteSection | undefined>(
  'websiteBuilder/blockById'
)
const currentParams = computed(() => {
  const block = blockById.value(props.id)
  return block?.type === 'integrations' ? block.params : undefined
})

const clearIntegration = async (): Promise<void> => {
  if (!locationId.value && !themeId.value) return

  locationId.value = ''
  themeId.value = ''
  await IntegrationsCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        locationId: null,
        providerType: 'untappd',
        themeId: null,
      },
    },
  })
}

const onSourceUpdate = async (value: string): Promise<void> => {
  source.value = value
  if (!(await formState.value.$validate())) {
    await clearIntegration()
    return
  }

  const ids = extractUntappdIds(value)
  if (!ids) return

  locationId.value = ids.locationId
  themeId.value = ids.themeId
  await IntegrationsCommands.updateBlock({
    targetId: props.id,
    update: {
      params: {
        locationId: ids.locationId,
        providerType: 'untappd',
        themeId: ids.themeId,
      },
    },
  })
}

onMounted(() => {
  const params = currentParams.value
  locationId.value = params?.locationId ?? ''
  themeId.value = params?.themeId ?? ''
  if (locationId.value && themeId.value) {
    source.value = `${locationId.value}/${themeId.value}`
  }
})
</script>
